<template>
    <div class="modal-card card idle-warning">
        <div class="card-body text-center">
            <!-- Countdown ring -->
            <div class="idle-warning__ring" :class="{ 'is-urgent': urgent }" role="timer" aria-live="off">
                <svg viewBox="0 0 120 120" aria-hidden="true">
                    <circle class="idle-warning__track" cx="60" cy="60" r="52" />
                    <circle class="idle-warning__progress" cx="60" cy="60" r="52"
                        :stroke-dasharray="circumference" :stroke-dashoffset="dashOffset" />
                </svg>
                <div class="idle-warning__time">
                    <span class="idle-warning__seconds">{{ secondsLeft }}</span>
                    <span class="idle-warning__unit">sec</span>
                </div>
            </div>

            <h3 class="mb-2">Are you still there?</h3>
            <p class="text-muted mb-0">
                You've been inactive for a while. For your security, you'll be logged out in
                <strong :class="urgent ? 'text-danger' : 'text-body'">{{ secondsLeft }} {{ secondsLeft === 1 ? 'second' : 'seconds' }}</strong>.
            </p>
            <!-- Announce once, not every tick. -->
            <span class="visually-hidden" aria-live="assertive">
                You will be logged out due to inactivity in about {{ totalSeconds }} seconds.
            </span>
        </div>
        <div class="card-footer d-flex justify-content-end gap-2">
            <button class="btn btn-danger" @click="logout()">
                <i class="fa fa-arrow-right-from-bracket me-2"></i>Log out
            </button>
            <button ref="stayButton" class="btn btn-primary" @click="stayLoggedIn()">
                Stay logged in
            </button>
        </div>
    </div>
</template>

<script>
const RADIUS = 52;
const DEFAULT_WINDOW_MS = 60 * 1000;

/**
 * Idle-timeout warning opened by Portal.vue. `modalData.logoutAt` is the same
 * deadline as Portal's logout timer, so the countdown and the actual logout
 * agree; this component only displays it.
 */
export default {
    props: {
        modalData: {
            type: [Object, Array, String],
        },
    },
    data() {
        const logoutAt = (this.modalData && this.modalData.logoutAt) || Date.now() + DEFAULT_WINDOW_MS;
        return {
            logoutAt,
            totalMs: Math.max(1000, logoutAt - Date.now()),
            now: Date.now(),
            ticker: null,
            resolved: false,
        };
    },
    computed: {
        remainingMs() {
            return Math.max(0, this.logoutAt - this.now);
        },
        secondsLeft() {
            return Math.ceil(this.remainingMs / 1000);
        },
        totalSeconds() {
            return Math.round(this.totalMs / 1000);
        },
        urgent() {
            return this.secondsLeft <= 10;
        },
        circumference() {
            return 2 * Math.PI * RADIUS;
        },
        // Ring drains as time runs out.
        dashOffset() {
            return this.circumference * (1 - this.remainingMs / this.totalMs);
        },
    },
    mounted() {
        // 250ms keeps the ring smooth without visible second skips.
        this.ticker = setInterval(() => { this.now = Date.now(); }, 250);
        this.$nextTick(() => this.$refs.stayButton && this.$refs.stayButton.focus());
    },
    beforeUnmount() {
        clearInterval(this.ticker);
        // Closed via Esc or backdrop click: that is the user responding, so keep
        // them logged in rather than leaving Portal's logout timer running unseen.
        if (!this.resolved && this.logoutAt > Date.now() && this.modalData && this.modalData.onConfirm) {
            this.modalData.onConfirm();
        }
    },
    methods: {
        stayLoggedIn() {
            this.resolved = true;
            this.$store.dispatch('hideModal');
            this.modalData.onConfirm();
        },
        logout() {
            this.resolved = true;
            this.$store.dispatch('hideModal');
            this.modalData.onCancel();
        },
    },
};
</script>

<style scoped>
.idle-warning .card-body {
    padding: 2rem 2rem 1.5rem;
}

.idle-warning__ring {
    position: relative;
    width: 7.5rem;
    height: 7.5rem;
    margin: 0 auto 1.25rem;
    --ring-color: #f6c343;
}

.idle-warning__ring.is-urgent {
    --ring-color: #e63757;
}

.idle-warning__ring svg {
    width: 100%;
    height: 100%;
    /* Start the arc at 12 o'clock. */
    transform: rotate(-90deg);
}

.idle-warning__track {
    fill: none;
    stroke: #edf2f9;
    stroke-width: 8;
}

.idle-warning__progress {
    fill: none;
    stroke: var(--ring-color);
    stroke-width: 8;
    stroke-linecap: round;
    transition: stroke-dashoffset 0.25s linear, stroke 0.3s ease;
}

.idle-warning__time {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    line-height: 1;
}

.idle-warning__seconds {
    font-size: 2.25rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--ring-color);
    transition: color 0.3s ease;
}

.idle-warning__unit {
    margin-top: 0.25rem;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #95aac9;
}

@media (prefers-reduced-motion: reduce) {
    .idle-warning__progress,
    .idle-warning__seconds {
        transition: none;
    }
}
</style>

<!-- Unscoped: scoped `:global(body.dark-mode) .x` collapses to just `body.dark-mode`. -->
<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .idle-warning__track {
        stroke: $dark-border-color;
    }

    .idle-warning__unit {
        color: $dark-text-secondary;
    }
}
</style>
